/**
 * BlogAnimation — Remotion composition for blog article visuals
 * Renders 5-scene animated slides: title, 3 insights, CTA
 * Played via @remotion/player directly in the browser (no server render needed)
 */
import { useCurrentFrame, interpolate, AbsoluteFill, Sequence } from 'remotion';

// ── Single animated scene ──────────────────────────────────────────────────────
function AnimatedScene({ title, subtitle, themeColor, sceneIndex, totalScenes }) {
  const frame = useCurrentFrame();

  const titleOpacity = interpolate(frame, [0, 14], [0, 1], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });
  const titleY = interpolate(frame, [0, 14], [28, 0], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });
  const subOpacity = interpolate(frame, [10, 22], [0, 1], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });

  // Fade out near end
  const sceneOpacity = interpolate(frame, [38, 45], [1, 0], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: '#0a0a14',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 80,
        opacity: sceneOpacity,
      }}
    >
      {/* Glow orb */}
      <div style={{
        position: 'absolute',
        width: 500,
        height: 500,
        borderRadius: '50%',
        background: themeColor,
        opacity: 0.06,
        filter: 'blur(120px)',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }} />

      {/* Progress bar */}
      <div style={{
        position: 'absolute',
        top: 44,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 8,
      }}>
        {Array.from({ length: totalScenes }).map((_, i) => (
          <div key={i} style={{
            height: 4,
            width: i === sceneIndex ? 32 : 8,
            borderRadius: 2,
            background: i <= sceneIndex ? themeColor : `${themeColor}30`,
            transition: 'width 0.3s ease',
          }} />
        ))}
      </div>

      {/* Section label */}
      <div style={{
        opacity: titleOpacity,
        color: `${themeColor}cc`,
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: 5,
        textTransform: 'uppercase',
        marginBottom: 20,
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        zIndex: 1,
      }}>
        {sceneIndex === 0 ? 'Featured Insight' : sceneIndex === totalScenes - 1 ? 'Take Action' : `Insight ${sceneIndex}`}
      </div>

      {/* Title */}
      <div style={{
        opacity: titleOpacity,
        transform: `translateY(${titleY}px)`,
        color: '#ffffff',
        fontSize: 54,
        fontWeight: 900,
        letterSpacing: '-0.03em',
        lineHeight: 1.15,
        textAlign: 'center',
        maxWidth: 860,
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        zIndex: 1,
      }}>
        {title}
      </div>

      {/* Subtitle */}
      {subtitle && (
        <div style={{
          opacity: subOpacity,
          marginTop: 20,
          color: 'rgba(255,255,255,0.45)',
          fontSize: 22,
          fontWeight: 500,
          textAlign: 'center',
          maxWidth: 640,
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          zIndex: 1,
        }}>
          {subtitle}
        </div>
      )}

      {/* Accent line */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)`,
        opacity: 0.4,
      }} />

      {/* Brand watermark */}
      <div style={{
        position: 'absolute',
        bottom: 36,
        right: 60,
        fontSize: 12,
        color: `${themeColor}50`,
        fontWeight: 800,
        letterSpacing: 4,
        fontFamily: '-apple-system, sans-serif',
      }}>
        IdeaWorks
      </div>
    </AbsoluteFill>
  );
}

// ── Main Composition ───────────────────────────────────────────────────────────
export const BlogAnimation = ({
  title = 'Key Insights',
  insight1 = 'AI is reshaping every industry',
  insight2 = 'Early adopters gain the edge',
  insight3 = 'Implementation is everything',
  cta = 'Read the full article',
  stat,
  themeColor = '#7c5efa',
}) => {
  const FRAMES_PER_SCENE = 45; // 1.5s per scene at 30fps

  const scenes = [
    { title, subtitle: stat || null },
    { title: insight1, subtitle: null },
    { title: insight2, subtitle: null },
    { title: insight3, subtitle: null },
    { title: cta, subtitle: 'qlia.io · iconiqs.ai' },
  ].filter(s => s.title);

  return (
    <AbsoluteFill style={{ background: '#0a0a14' }}>
      {/* Background gradient */}
      <AbsoluteFill style={{
        background: `radial-gradient(ellipse at 60% 40%, ${themeColor}07 0%, transparent 55%),
                     radial-gradient(ellipse at 40% 60%, #00d4b806 0%, transparent 55%)`,
      }} />

      {scenes.map((scene, i) => (
        <Sequence key={i} from={i * FRAMES_PER_SCENE} durationInFrames={FRAMES_PER_SCENE}>
          <AnimatedScene
            title={scene.title}
            subtitle={scene.subtitle}
            themeColor={themeColor}
            sceneIndex={i}
            totalScenes={scenes.length}
          />
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};
